import React, { useEffect, useState } from "react";
import { Alert, Button, Checkbox, Form, Input, Modal } from "antd";
import { useDispatch } from "react-redux";
import { receiveMifirFeedback, sendNotification, submitMifirReport } from "../redux/actions";

const NCA_SAVE_PREFERENCE_KEY = "finmatek.mifir.saveNcaInBrowser";
const LEGACY_CYSEC_SAVE_PREFERENCE_KEY = "finmatek.mifir.saveCysecInBrowser";

const readSaveInBrowserPreference = () => {
  if (typeof window === "undefined" || !window.localStorage) return false;

  try {
    return (
      window.localStorage.getItem(NCA_SAVE_PREFERENCE_KEY) === "true"
      || window.localStorage.getItem(LEGACY_CYSEC_SAVE_PREFERENCE_KEY) === "true"
    );
  } catch (err) {
    return false;
  }
};

const storeSaveInBrowserPreference = (enabled) => {
  if (typeof window === "undefined" || !window.localStorage) return;

  try {
    if (enabled) {
      window.localStorage.setItem(NCA_SAVE_PREFERENCE_KEY, "true");
    } else {
      window.localStorage.removeItem(NCA_SAVE_PREFERENCE_KEY);
      window.localStorage.removeItem(LEGACY_CYSEC_SAVE_PREFERENCE_KEY);
    }
  } catch (err) {
    // Browser storage can be disabled by policy/private mode. The action must still continue.
  }
};

const storeCredentialsInBrowserPasswordManager = async (username, password, authorityName) => {
  if (
    typeof window === "undefined" ||
    typeof navigator === "undefined" ||
    !navigator.credentials ||
    typeof window.PasswordCredential !== "function"
  ) {
    return;
  }

  try {
    const credential = new window.PasswordCredential({
      id: username,
      password,
      name: authorityName || "NCA",
    });
    await navigator.credentials.store(credential);
  } catch (err) {
    // The browser/password manager may decline or block credential storage.
    // This must never prevent the MiFIR action itself.
  }
};

const MiFIRSubmissionModal = ({ visible, onClose, onStarted, disabled, mode = "submit", authorityName = "NCA" }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [saveInBrowser, setSaveInBrowser] = useState(false);
  const isFeedback = mode === "feedback";
  const ncaLabel = String(authorityName || "NCA").trim() || "NCA";

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      return;
    }

    setSaveInBrowser(readSaveInBrowserPreference());
  }, [visible, form]);

  const onSubmit = async (confirmErrors = false) => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const action = isFeedback
        ? receiveMifirFeedback(values.cysecUsername, values.cysecPassword)
        : submitMifirReport(values.cysecUsername, values.cysecPassword, confirmErrors);
      const { payload } = await dispatch(action);

      storeSaveInBrowserPreference(saveInBrowser);
      if (saveInBrowser) {
        await storeCredentialsInBrowserPasswordManager(
          values.cysecUsername,
          values.cysecPassword,
          ncaLabel
        );
      }

      dispatch(sendNotification({
        type: "info",
        message: isFeedback ? "MiFIR Feedback" : "MiFIR Submission",
        description: isFeedback
          ? `Feedback retrieval started. Check MiFIR Process Status for the final ${ncaLabel} result.`
          : `Submission started. Check MiFIR Process Status for the final ${ncaLabel} result.`,
      }));
      form.resetFields();
      onClose();
      if (onStarted) onStarted(payload);
    } catch (err) {
      if (err && err.errorFields) return;

      if (!isFeedback && err && err.code === "REPORT_HAS_ERRORS_CONFIRMATION_REQUIRED") {
        setSubmitting(false);
        const report = err.report || {};
        Modal.confirm({
          title: "Report Errors Detected",
          okText: "Submit Anyway",
          cancelText: "Cancel",
          content: (
            <div>
              <p>
                Report errors were detected in this report. This customer/trading platform is
                configured to allow submission with errors.
              </p>
              <p>
                <strong>Please confirm that you want to continue with {ncaLabel} submission despite the report errors.</strong>
              </p>
              <div style={{ marginTop: 12 }}>
                {report.report_date ? <div>Report Date: <strong>{String(report.report_date).substring(0, 10)}</strong></div> : null}
                {report.trading_platform ? <div>Trading Platform: <strong>{report.trading_platform}</strong></div> : null}
                {report.report_number ? <div>Report Number: <strong>{report.report_number}</strong></div> : null}
                {report.competent_authority_name ? <div>Competent Authority: <strong>{report.competent_authority_name}</strong></div> : null}
              </div>
            </div>
          ),
          onOk: () => onSubmit(true),
        });
        return;
      }

      dispatch(sendNotification({
        type: "error",
        message: isFeedback ? "MiFIR Feedback" : "MiFIR Submission",
        description: (err && err.error) || (isFeedback
          ? "Could not start MiFIR feedback retrieval."
          : "Could not start the MiFIR submission."),
      }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={isFeedback ? `Receive MiFIR Feedback from ${ncaLabel}` : `Submit Pending MiFIR Report to ${ncaLabel}`}
      visible={visible}
      onCancel={onClose}
      width={560}
      destroyOnClose={false}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={() => onSubmit(false)}
          loading={submitting}
          disabled={disabled}
        >
          {isFeedback ? "Receive Feedback" : "Submit"}
        </Button>,
      ]}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={`${ncaLabel} credentials are not stored within the Finmatek system.`}
        description={(
          <div>
            <div>
              The Finmatek portal does not retain or store your {ncaLabel} username or password.
              If you select the <strong>“Save in Browser”</strong> option, your credentials are stored locally
              by your browser or password manager and are not accessible to or stored by Finmatek.
            </div>
          </div>
        )}
      />

      <Form form={form} layout="vertical" autoComplete={saveInBrowser ? "on" : "off"}>
        <Form.Item
          label={`${ncaLabel} Username`}
          name="cysecUsername"
          rules={[{ required: true, message: `${ncaLabel} username is required` }]}
        >
          <Input
            autoComplete={saveInBrowser ? "username" : "off"}
            disabled={submitting || disabled}
          />
        </Form.Item>

        <Form.Item
          label={`${ncaLabel} Password`}
          name="cysecPassword"
          rules={[{ required: true, message: `${ncaLabel} password is required` }]}
        >
          <Input.Password
            autoComplete={saveInBrowser ? "current-password" : "new-password"}
            disabled={submitting || disabled}
          />
        </Form.Item>

        <div style={{ marginTop: -4, marginBottom: 4 }}>
          <Checkbox
            checked={saveInBrowser}
            onChange={(event) => setSaveInBrowser(event.target.checked)}
            disabled={submitting || disabled}
          >
            Save in Browser
          </Checkbox>
        </div>
      </Form>
    </Modal>
  );
};

export default MiFIRSubmissionModal;
